import { detailsBase } from './billing/serviceDetailsConcurrency';
import type { TFunction } from 'i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  InputAdornment,
  ListItemIcon,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import DoneIcon from '@mui/icons-material/Done';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PaymentIcon from '@mui/icons-material/Payment';
import PreviewIcon from '@mui/icons-material/Preview';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SendIcon from '@mui/icons-material/Send';
import { useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { GroupedInvoiceLines } from './GroupedInvoiceLines';
import { InvoiceSummaryCard } from './InvoiceSummaryCard';
import { PreviousInvoicesSection } from './PreviousInvoicesSection';
import { GenerateInvoiceDialog, type GeneratedInvoiceDocuments } from './GenerateInvoiceDialog';
import { SveaPaymentSection } from '@/features/invoices/SveaPaymentSection';
import { PostInvoiceDocumentsDialog } from './PostInvoiceDocumentsDialog';
import { PreInvoiceChecklistDialog } from './PreInvoiceChecklistDialog';
import { RecordPaymentDialog } from './RecordPaymentDialog';
import {
  openInvoicePdf,
  openInvoicePreviewPdf,
  printInvoicePdf,
  printReceiptPdf,
  useDeliverInvoice,
  useInvoiceDetail,
  useInvoicePreview,
  useVoidInvoice,
  useWorkOrderInvoicesHistory,
  type InvoiceDetail,
  type WorkOrderInvoice,
  type WorkOrderInvoiceHistoryEntry,
} from '@/hooks/useInvoices';
import { useAuthStore } from '@/stores/authStore';
import { INVOICE_READ_PERMISSIONS } from '@/lib/invoicePermissions';
import { PaymentHistorySection, PaymentLedgerProvider } from './PaymentHistorySection';
import { useBillActualHours, type WorkOrderDetail } from '@/hooks/useWorkOrders';
import { useUpdateBooking } from '@/hooks/useBookings';
import { useNotify } from '@/hooks/useNotify';
import { useWorkOrderBillingLines } from '@/hooks/useWorkOrderBillingLines';
import { translateBillingError } from '@/lib/billingErrors';
import { describeApiError } from '@/lib/describeApiError';
import { EDIT_BILLABLE_HOURS_PERMISSION } from '@/lib/billableHours';
import { closedActualHours, formatDecimalHoursLabel } from '@/utils/actualTime';
import { eurosToCents, grossToNetCents } from '@/utils/money';
import { BillActualServices } from './BillActualServices';
import { WorkOrderSurchargeRow } from './WorkOrderSurchargeRow';
import { DEFAULT_VAT_PERCENT, netToGrossCents } from '@/utils/vat';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { CreditNoteDialog } from '@/features/invoices/CreditNoteDialog';
import { WorkOrderConsolidationNotice } from './consolidation/WorkOrderConsolidationNotice';
import {
  annotateBillingLines,
  findServiceByBookingId,
  findServiceByName,
  payloadAfterAddExtra,
  payloadAfterExtraPatch,
  payloadAfterFixedPriceOverride,
  payloadAfterLaborPatch,
  payloadAfterPartPatch,
  workOrderItemCostFromUnitNet,
  type AnnotatedBillingLine,
} from './billing/billingLineSource';
import { BillingLineActionsContext, type ConsumedPartPriceInfo } from './billing/billingLineActions';
import {
  PRICING_READ_PERMISSION,
  PricingAccessError,
  fenceHolds,
  isSessionCancel,
  ownerEquals,
  takeFence,
  usePricingAccess,
  type PricingFence,
  type PricingOwner,
} from '@/features/inventory/pricing/pricingSession';
import { isFixedPricePackage } from './fixedPricePackage';

const LOCKED_STATUSES: readonly string[] = ['invoiced', 'completed', 'cancelled', 'on_consolidated_invoice'];
/** Looking a consumed part's price up again edits the work order and reads current part prices (#3034). */
const WORK_ORDER_UPDATE_PERMISSION = 'work_orders.update';
const REPRICE_PERMISSIONS: readonly string[] = [WORK_ORDER_UPDATE_PERMISSION, PRICING_READ_PERMISSION];

/**
 * One pending, confirmed-by-nobody-yet reprice of one consumed line, with what it was opened under: the owner (tenant, user,
 * session family) and session generation of the render that offered it, the work order, and the line. It is only valid while
 * ALL of that still holds; see `repriceIntentHolds`.
 */
interface RepriceIntent {
  readonly line: AnnotatedBillingLine;
  readonly workOrderId: string;
  /** Id of the consumed-part row (what the route's `:partId` is). */
  readonly consumedPartId: string;
  /** The catalog part the line was linked to when the dialog opened. */
  readonly catalogPartId: string;
  readonly owner: PricingOwner;
  readonly epoch: number;
}
/** The localized reason a price lookup of a consumed part was refused; the line is unchanged in every case. */
function repriceFailureKey(error: unknown): string {
  if (error instanceof PricingAccessError) return 'detail.partPricing.reprice.forbidden';
  const { status, data } = (error ?? {}) as { status?: unknown; data?: unknown };
  const code = typeof (data as { code?: unknown } | null)?.code === 'string' ? (data as { code: string }).code : '';
  if (code === 'reprice_requires_catalog_part') return 'detail.partPricing.reprice.requiresCatalogPart';
  if (code === 'price_unavailable') return 'detail.partPricing.reprice.unavailable';
  if (code === 'party_unresolvable') return 'detail.partPricing.reprice.partyUnresolvable';
  if (status === 403) return 'detail.partPricing.reprice.forbidden';
  return 'detail.partPricing.reprice.failed';
}
/**
 * Whether the work order has nothing billable yet (#4117): the preview shows no
 * line and no part of the work order could produce one. A booking made only to
 * reserve calendar time carries service placeholders with no price, steps with
 * hours, parts or extras, so the preview omits them (a fixed price of 0 without
 * an override is billed from its components). Anything that is, or could be, a
 * line keeps the ordinary view: a free or discounted job (override 0, discount
 * 100 %), zero-net positive and negative lines, consumed parts and legacy items.
 */
function hasNothingBillable(
  workOrder: WorkOrderDetail,
  consumedPartCount: number,
  totalsCents: readonly (number | null | undefined)[],
): boolean {
  if (consumedPartCount > 0 || workOrder.items.length > 0) return false;
  if (totalsCents.some((cents) => cents != null && cents !== 0)) return false;
  return workOrder.services.every((service) => {
    if (isFixedPricePackage(service) || service.fixedPriceOverrideCents != null) return false;
    if (service.parts.length > 0 || service.extras.length > 0) return false;
    return !service.laborItems.some((item) => Number(item.estimatedHours) > 0);
  });
}

const VOID_BLOCKED_STATUSES = new Set(['void', 'refunded', 'credited', 'paid', 'partial']);

interface WorkOrderPaymentsProps {
  workOrder: WorkOrderDetail;
  workOrderInvoice?: WorkOrderInvoice;
  cardVariant: 'elevation' | 'outlined';
  t: TFunction;
  formatPrice: (cents: number) => string;
  /** Open a service's steps in the Works card — where combined labour is edited (#2638). */
  onOpenServiceLabor?: ((bookingServiceId: string) => void) | undefined;
}

interface OverflowAction {
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick?: () => void;
  to?: string;
}

function billingErrorCode(err: unknown): string | undefined {
  if (typeof err !== 'object' || err === null || !('data' in err)) return undefined;
  const data = (err as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null || !('code' in data)) return undefined;
  const code = (data as { code?: unknown }).code;
  return typeof code === 'string' ? code : undefined;
}

function isInvoiceVoidable(invoice: WorkOrderInvoice, detail: InvoiceDetail | undefined): boolean {
  if (VOID_BLOCKED_STATUSES.has(invoice.status) || invoice.paidAmountCents > 0) return false;
  if (detail?.deliveries.some((delivery) => delivery.status === 'sent')) return false;
  return true;
}

function isInvoiceCreditable(invoice: WorkOrderInvoice, detail: InvoiceDetail | undefined): boolean {
  const invoiceType = detail?.invoiceType ?? 'invoice';
  if (invoiceType !== 'invoice') return false;
  return invoice.status !== 'draft' && invoice.status !== 'void' && invoice.status !== 'credited';
}

/** Newest positive sales invoice, ignoring credit notes (negative totals). */
function latestSalesInvoiceCredited(history: readonly WorkOrderInvoiceHistoryEntry[]): boolean {
  const sales = history.filter((invoice) => invoice.totalCents > 0);
  const latest = sales.reduce<WorkOrderInvoiceHistoryEntry | null>((best, invoice) => {
    if (!best || invoice.createdAt > best.createdAt) return invoice;
    return best;
  }, null);
  return latest?.status === 'credited';
}

function currentBillingRecipientName(workOrder: WorkOrderDetail): string | null {
  const parties = workOrder.parties ?? [];
  const billing = parties.find((party) => party.role === 'billing_party');
  const orderer = parties.find((party) => party.role === 'orderer');
  const fromParty = (billing ?? orderer)?.name.trim() ?? '';
  if (fromParty !== '') return fromParty;
  const { customer } = workOrder;
  const business = customer.businessName?.trim() ?? '';
  if (business !== '') return business;
  const person = [customer.firstName, customer.lastName]
    .filter((part): part is string => typeof part === 'string' && part.trim() !== '')
    .join(' ');
  return person === '' ? null : person;
}

/** Pause after the last keystroke before PATCHing the hourly rate (#2654). */
const LABOR_RATE_SAVE_DEBOUNCE_MS = 400;

function parseHourlyRateDraft(draft: string): number | null {
  const trimmed = draft.trim();
  if (trimmed === '' || /[.,]$/.test(trimmed)) return null;
  return eurosToCents(trimmed.replace(',', '.'));
}

function formatHourlyRateCents(cents: number): string {
  return cents > 0 ? (cents / 100).toFixed(2) : '';
}

export function WorkOrderPayments({
  workOrder,
  workOrderInvoice,
  cardVariant,
  t,
  formatPrice,
  onOpenServiceLabor,
}: WorkOrderPaymentsProps) {
  const { t: ti } = useTranslation('invoices');
  const { t: tc } = useTranslation('common');
  const updateBooking = useUpdateBooking();
  const notify = useNotify();
  const voidInvoice = useVoidInvoice();
  const deliverInvoice = useDeliverInvoice();
  const [generateOpen, setGenerateOpen] = useState(false);
  const [postInvoiceDocuments, setPostInvoiceDocuments] =
    useState<GeneratedInvoiceDocuments | null>(null);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);
  const [overflowAnchor, setOverflowAnchor] = useState<HTMLElement | null>(null);
  const [voidErrorOfferCredit, setVoidErrorOfferCredit] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const billing = useWorkOrderBillingLines(workOrder.id);
  const displayRateCents = workOrder.laborRateGrossCents != null && workOrder.laborRateGrossCents > 0
    ? workOrder.laborRateGrossCents
    : workOrder.laborRateCents != null && workOrder.laborRateCents > 0
      ? netToGrossCents(workOrder.laborRateCents, workOrder.vatPercent ?? DEFAULT_VAT_PERCENT)
      : 0;
  const [rateDraft, setRateDraft] = useState(() => formatHourlyRateCents(displayRateCents));
  const rateDraftRef = useRef(rateDraft);
  rateDraftRef.current = rateDraft;
  const dirtyRateRef = useRef(false);
  const laborRateSaveEpochRef = useRef(0);
  const laborRateInFlightRef = useRef(false);
  const laborRateQueuedCentsRef = useRef<number | null>(null);
  const lastSubmittedRateCentsRef = useRef<number | null>(null);
  const lastFailedRateCentsRef = useRef<number | null>(null);
  const persistHourlyRateRef = useRef<(reason: 'blur' | 'enter' | 'debounce') => void>(() => {});
  const flushHourlyRateSaveRef = useRef<() => Promise<void>>(async () => {});
  const [rateUpdating, setRateUpdating] = useState(false);
  useEffect(() => {
    if (dirtyRateRef.current) return;
    setRateDraft(formatHourlyRateCents(displayRateCents));
  }, [displayRateCents]);
  const [linePendingDelete, setLinePendingDelete] = useState<AnnotatedBillingLine | null>(null);
  const [repriceIntent, setRepriceIntent] = useState<RepriceIntent | null>(null);
  const pricingAccess = usePricingAccess();
  const mountedRef = useRef(true);
  const workOrderIdRef = useRef(workOrder.id);
  workOrderIdRef.current = workOrder.id;
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  // #1315: the history route is permission-guarded, so don't fire it for roles
  // that would only get a 403 back.
  const hasAnyPermission = useAuthStore((s) => s.hasAnyPermission);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canEditBillableHours = hasPermission(EDIT_BILLABLE_HOURS_PERMISSION);
  // Read as primitives so a downgrade re-renders in the same pass (the identity of `hasPermission` never changes).
  const mayUpdateWorkOrder = useAuthStore((s) => s.hasPermission(WORK_ORDER_UPDATE_PERMISSION));
  const canRepriceLines = pricingAccess.canRead && mayUpdateWorkOrder;
  const billActual = useBillActualHours();
  const [billActualServiceId, setBillActualServiceId] = useState<string | null>(null);
  const canReadInvoices = hasAnyPermission(INVOICE_READ_PERMISSIONS);
  const invoicesHistory = useWorkOrderInvoicesHistory(
    workOrder.id,
    canReadInvoices,
  );
  const invoiceDetail = useInvoiceDetail(workOrderInvoice?.id);
  const preview = useInvoicePreview(
    workOrder.id,
    workOrder.vatPercent ?? 25.5,
    0,
    !workOrderInvoice,
  );
  const invoiceLineItems = invoiceDetail.data?.lineItems;
  const previewLineItems = preview.data?.lineItems;
  const billingTotals = invoiceDetail.data ?? preview.data;
  // Verollinen loppusumma tulee laskulta tai esikatselusta. Jos kumpaakaan ei
  // ole, summaa EI arvioida totalPartsCents + totalLaborCents -summasta: ne ovat
  // nettoa, ja tämän rivin otsikko lupaa verollista (docs/VAT-BASIS.md § 5).
  const displayTotalCents = billingTotals?.totalCents ?? workOrder.totalWithVatCents ?? null;
  // Selected as a boolean, so losing the right re-renders the card in the same pass (it decides whether a reprice may stay open).
  const canManageInvoices = useAuthStore((s) => s.hasAnyPermission(['invoices.manage']));
  const canTakePayments = hasAnyPermission(['payments.take', 'invoices.manage']);
  const remainingCents = workOrderInvoice
    ? workOrderInvoice.totalCents - workOrderInvoice.paidAmountCents
    : 0;
  const invoiceSettled = !!workOrderInvoice && (
    workOrderInvoice.status === 'paid'
    || remainingCents <= 0
  );
  const billingLocked =
    LOCKED_STATUSES.includes(workOrder.status)
    || workOrderInvoice !== undefined
    || !canManageInvoices;
  const editable = !billingLocked && editMode;
  useEffect(() => {
    setEditMode(false);
    dirtyRateRef.current = false;
    lastSubmittedRateCentsRef.current = null;
    lastFailedRateCentsRef.current = null;
    laborRateQueuedCentsRef.current = null;
    laborRateSaveEpochRef.current += 1;
    setRateUpdating(false);
    return () => {
      laborRateQueuedCentsRef.current = null;
      laborRateSaveEpochRef.current += 1;
    };
  }, [workOrder.id]);
  useEffect(() => {
    if (billingLocked) setEditMode(false);
  }, [billingLocked]);
  const fallbackTaxPercent = invoiceDetail.data?.taxPercent ?? workOrder.vatPercent ?? 25.5;
  const annotatedLines = useMemo(
    () => {
      const lines = annotateBillingLines(invoiceLineItems ?? previewLineItems ?? [], {
        ...workOrder,
        consumedParts: billing.consumedParts ?? [],
      }, {
        includeUnmatchedItems: !billingLocked,
        fallbackTaxPercent,
      });
      if (canEditBillableHours) return lines;
      return lines.map((line) => {
        if (line.source?.kind !== 'labor') return line;
        return {
          ...line,
          source: {
            ...line.source,
            capabilities: {
              ...line.source.capabilities,
              quantity: false,
              deletable: false,
            },
          },
        };
      });
    },
    [invoiceLineItems, previewLineItems, workOrder, billingLocked, fallbackTaxPercent, billing.consumedParts, canEditBillableHours],
  );
  // #4117: exactly one query is the active billing source: the invoice detail
  // when an invoice exists, the preview otherwise. The other is disabled, and a
  // disabled TanStack query without cached data is `status: 'pending'` forever
  // (`fetchStatus: 'idle'`), so its `isPending` must never decide anything here.
  const activeBillingSource = workOrderInvoice === undefined ? preview : invoiceDetail;
  const billingSourceLoading = activeBillingSource.isPending;
  // Empty only once the active preview has settled without an error and no
  // invoice exists; a failed or pending load must never read as "nothing to bill".
  const billingEmpty =
    workOrderInvoice === undefined
    && !billingSourceLoading
    && !preview.isError
    && annotatedLines.length === 0
    && hasNothingBillable(workOrder, billing.consumedParts?.length ?? 0, [
      preview.data?.totalCents,
      preview.data?.subtotalCents,
      workOrder.totalWithVatCents,
      workOrder.totalCostCents,
      workOrder.totalPartsCents,
      workOrder.totalLaborCents,
      workOrder.surchargeCents,
    ]);
  const hoursBillingLocked =
    LOCKED_STATUSES.includes(workOrder.status)
    || workOrderInvoice !== undefined;
  const billActualHoursLabel = formatDecimalHoursLabel(
    closedActualHours(
      workOrder.services.find((service) => service.bookingServiceId === billActualServiceId),
    ),
  );
  const billingBlocked = workOrder.isDisputed || workOrder.isInvoicingHold;
  const primaryAction = ((): { kind: 'generate' | 'pay'; label: string; disabled?: boolean } | null => {
    if (billingBlocked) {
      if (!canManageInvoices) return null;
      return { kind: 'generate', label: t('serviceActions.paymentEntry'), disabled: true };
    }
    if (workOrderInvoice && !invoiceSettled) {
      if (!canTakePayments) return null;
      return { kind: 'pay', label: t('serviceActions.recordPayment') };
    }
    if (workOrder.status === 'ready_to_invoice' && !workOrderInvoice) {
      if (!canManageInvoices) return null;
      return { kind: 'generate', label: t('serviceActions.paymentEntry') };
    }
    return null;
  })();

  const canVoidInvoice = workOrderInvoice
    ? isInvoiceVoidable(workOrderInvoice, invoiceDetail.data)
    : false;
  const canCreditInvoice = workOrderInvoice
    ? isInvoiceCreditable(workOrderInvoice, invoiceDetail.data)
    : false;
  const latestReceiptId = (invoiceDetail.data?.payments ?? workOrderInvoice?.payments ?? [])
    .map((payment) => payment.receiptId)
    .find((id): id is string => Boolean(id));
  // Receipt / invoice recipient suggestion. A separate billing party wins with
  // its invoice email, then its contact email. When the orderer is billed, the
  // work order's own contact email (the inline edit on the customer card,
  // which does not touch the person record) counts before the person's
  // email — staff typing it there expect the receipt to go to it (#3113).
  const separateBillingParty = workOrder.parties?.find((party) => party.role === 'billing_party');
  const ordererParty = workOrder.parties?.find((party) => party.role === 'orderer');
  const customerEmail = separateBillingParty
    ? separateBillingParty.invoiceEmail?.trim() || separateBillingParty.email
    : ordererParty?.invoiceEmail?.trim()
      || workOrder.contactEmail?.trim()
      || ordererParty?.email
      || workOrder.customer?.email
      || null;

  // After a mistaken card payment is voided the invoice is open again (#3115).
  // Offer the two real continuations side by side instead of hiding "send as
  // invoice" in the overflow menu as "resend".
  const hasReversedPayment = (invoiceDetail.data?.payments ?? workOrderInvoice?.payments ?? [])
    .some((payment) => payment.paymentMethod === 'reversal');
  const canSendAsInvoice = Boolean(
    workOrderInvoice
      && canManageInvoices
      && !invoiceSettled
      && (workOrderInvoice.status === 'sent' || workOrderInvoice.status === 'partial'),
  );
  const handleSendAsInvoice = () => {
    if (!workOrderInvoice) return;
    void deliverInvoice.mutateAsync({
      invoiceId: workOrderInvoice.id,
      workOrderId: workOrder.id,
      ...(customerEmail ? { recipientEmail: customerEmail } : {}),
    }).then(() => {
      notify.success(ti('actions.deliverSuccess'));
    }).catch((err: unknown) => {
      notify.error(tc('error'), translateBillingError(err, ti));
    });
  };

  const overflowActions = useMemo<OverflowAction[]>(() => {
    if (!workOrderInvoice) return [];
    const actions: OverflowAction[] = [
      {
        key: 'print',
        label: ti('actions.print'),
        icon: <PrintIcon fontSize="small" />,
        onClick: () => {
          void printInvoicePdf(workOrderInvoice.id).then((result) => {
            if (result.status === 'left-open') notify.info(tc('print.pdfUseBrowserDialog'));
          }).catch((err: unknown) => {
            notify.error(tc('error'), translateBillingError(err, ti));
          });
        },
      },
    ];
    if (latestReceiptId) {
      actions.push({
        key: 'print-receipt',
        label: ti('actions.printReceipt'),
        icon: <ReceiptLongIcon fontSize="small" />,
        onClick: () => {
          void printReceiptPdf(latestReceiptId).then((result) => {
            if (result.status === 'left-open') notify.info(tc('print.pdfUseBrowserDialog'));
          }).catch((err: unknown) => {
            notify.error(tc('error'), translateBillingError(err, ti));
          });
        },
      });
    }
    if (canManageInvoices && workOrderInvoice.status !== 'void' && workOrderInvoice.status !== 'draft') {
      actions.push({
        key: 'resend',
        label: ti('actions.resend'),
        icon: <SendIcon fontSize="small" />,
        onClick: () => {
          void deliverInvoice.mutateAsync({
            invoiceId: workOrderInvoice.id,
            workOrderId: workOrder.id,
            ...(customerEmail ? { recipientEmail: customerEmail } : {}),
          }).then(() => {
            notify.success(ti('actions.deliverSuccess'));
          }).catch((err: unknown) => {
            notify.error(tc('error'), translateBillingError(err, ti));
          });
        },
      });
    }
    if (canManageInvoices && canVoidInvoice) {
      actions.push({
        key: 'void',
        label: ti('actions.void'),
        icon: <BlockIcon fontSize="small" />,
        onClick: () => {
          setVoidErrorOfferCredit(null);
          setVoidOpen(true);
        },
      });
    }
    if (canManageInvoices && canCreditInvoice) {
      actions.push({
        key: 'credit',
        label: ti('actions.creditNote'),
        icon: <ReceiptLongIcon fontSize="small" />,
        onClick: () => setCreditOpen(true),
      });
    }
    actions.push({
      key: 'open',
      label: ti('actions.openInvoice'),
      icon: <OpenInNewIcon fontSize="small" />,
      to: `/invoices/${workOrderInvoice.id}`,
    });
    return actions;
  }, [
    canCreditInvoice,
    canManageInvoices,
    canVoidInvoice,
    customerEmail,
    deliverInvoice,
    latestReceiptId,
    notify,
    tc,
    ti,
    workOrder.id,
    workOrderInvoice,
  ]);

  const showCreditedReinvoiceHint = !workOrderInvoice
    && latestSalesInvoiceCredited(invoicesHistory.data ?? []);
  const billingRecipientName = showCreditedReinvoiceHint
    ? currentBillingRecipientName(workOrder)
    : null;

  const handlePrimaryAction = () => {
    if (!primaryAction || primaryAction.disabled) return;
    if (primaryAction.kind === 'pay') {
      setPaymentOpen(true);
      return;
    }
    // Parts and labour were confirmed when the work order was marked ready to
    // invoice (#4102). Hand-over only still needs a final inspection that was
    // left open then; once it is recorded, nothing is asked twice.
    if (workOrder.finalInspectionCompleted === true) {
      setGenerateOpen(true);
      return;
    }
    setChecklistOpen(true);
  };

  const handleChecklistConfirm = () => {
    setChecklistOpen(false);
    setGenerateOpen(true);
  };

  const laborCount = workOrder.services.reduce(
    (sum, service) => sum + service.laborItems.length,
    0,
  );
  const partsCount = workOrder.services.reduce(
    (sum, service) => sum + service.parts.length,
    0,
  ) + billing.consumedParts.length;

  const handleVoid = async () => {
    if (!workOrderInvoice) return;
    try {
      await voidInvoice.mutateAsync({
        invoiceId: workOrderInvoice.id,
        reason: ti('actions.voidDefaultReason'),
        workOrderId: workOrder.id,
      });
      notify.success(ti('actions.voidSuccess'));
      setVoidOpen(false);
      setVoidErrorOfferCredit(null);
    } catch (err) {
      const code = billingErrorCode(err);
      const message = code === 'invoice_already_delivered'
        ? ti('error.invoiceAlreadyDelivered')
        : translateBillingError(err, ti);
      notify.error(tc('error'), message);
      if (code === 'invoice_already_delivered' || code === 'invalid_invoice_state') {
        setVoidErrorOfferCredit(message);
      }
    }
  };

  const historyWithPayments = (invoicesHistory.data ?? []).filter((inv) => inv.paidAmountCents > 0);
  const historyInvoiceId = !workOrderInvoice && historyWithPayments[0]
    ? historyWithPayments[0].id
    : undefined;
  const historyDetail = useInvoiceDetail(historyInvoiceId);
  const paymentLedgerContext = {
    workOrderId: workOrder.id,
    ...(workOrderInvoice
      ? { invoiceId: workOrderInvoice.id }
      : historyInvoiceId
        ? { invoiceId: historyInvoiceId }
        : {}),
  };

  const mutationHandlers = {
    onSuccess: () => notify.success(ti('workOrderBilling.lineUpdated')),
    onError: () => notify.error(ti('workOrderBilling.lineUpdateFailed')),
  };

  const handleCommitQuantity = (line: AnnotatedBillingLine, quantity: number) => {
    const source = line.source;
    if (!source) return;
    if (source.kind === 'labor') {
      const service = findServiceByBookingId(workOrder, source.bookingServiceId);
      if (!service) return;
      billing.updateServiceDetails.mutate({
        base: detailsBase(workOrder, source.bookingServiceId),
        workOrderId: workOrder.id,
        bookingServiceId: source.bookingServiceId,
        data: payloadAfterLaborPatch(service, source.laborItemId, { estimatedHours: quantity }),
      }, mutationHandlers);
      return;
    }
    if (source.kind === 'part') {
      const service = findServiceByBookingId(workOrder, source.bookingServiceId);
      if (!service) return;
      const nextQuantity = quantity < 0.01 ? 0.01 : quantity;
      billing.updateServiceDetails.mutate({
        base: detailsBase(workOrder, source.bookingServiceId),
        workOrderId: workOrder.id,
        bookingServiceId: source.bookingServiceId,
        data: payloadAfterPartPatch(service, source.partId, { quantity: nextQuantity }),
      }, mutationHandlers);
      return;
    }
    if (source.kind === 'consumedPart') {
      // updatePartSchema.quantity is z.number().int().min(1); a decimal or 0 is 400.
      const nextQuantity = Math.max(1, Math.round(quantity));
      billing.updateConsumedPart.mutate({
        workOrderId: workOrder.id,
        partId: source.partId,
        data: { quantity: nextQuantity },
      }, mutationHandlers);
      return;
    }
    if (source.kind === 'extra') {
      const service = findServiceByBookingId(workOrder, source.bookingServiceId);
      if (!service) return;
      billing.updateServiceDetails.mutate({
        base: detailsBase(workOrder, source.bookingServiceId),
        workOrderId: workOrder.id,
        bookingServiceId: source.bookingServiceId,
        data: payloadAfterExtraPatch(service, source.extraId, { quantity }),
      }, mutationHandlers);
    }
  };

  const handleCommitUnitPrice = (line: AnnotatedBillingLine, unitPriceGrossCents: number) => {
    const source = line.source;
    if (!source) return;
    if (source.kind === 'part') {
      const service = findServiceByBookingId(workOrder, source.bookingServiceId);
      if (!service) return;
      billing.updateServiceDetails.mutate({
        base: detailsBase(workOrder, source.bookingServiceId),
        workOrderId: workOrder.id,
        bookingServiceId: source.bookingServiceId,
        data: payloadAfterPartPatch(service, source.partId, { unitPriceGrossCents }),
      }, mutationHandlers);
      return;
    }
    if (source.kind === 'extra') {
      const service = findServiceByBookingId(workOrder, source.bookingServiceId);
      if (!service) return;
      billing.updateServiceDetails.mutate({
        base: detailsBase(workOrder, source.bookingServiceId),
        workOrderId: workOrder.id,
        bookingServiceId: source.bookingServiceId,
        data: payloadAfterExtraPatch(service, source.extraId, { amountGrossCents: unitPriceGrossCents }),
      }, mutationHandlers);
      return;
    }
    if (source.kind === 'workOrderItem') {
      const vat = line.taxPercent ?? workOrder.vatPercent ?? DEFAULT_VAT_PERCENT;
      const unitNetCents = grossToNetCents(unitPriceGrossCents, line.taxable ? vat : 0);
      const netCents = workOrderItemCostFromUnitNet(line, source.costField, unitNetCents);
      billing.updateManualLine.mutate({
        workOrderId: workOrder.id,
        itemId: source.itemId,
        data: source.costField === 'partsCostCents'
          ? { partsCostCents: netCents }
          : { laborCostCents: netCents },
      }, mutationHandlers);
      return;
    }
    if (source.kind === 'consumedPart') {
      const vat = line.taxPercent ?? workOrder.vatPercent ?? DEFAULT_VAT_PERCENT;
      const netCents = grossToNetCents(unitPriceGrossCents, line.taxable ? vat : 0);
      billing.updateConsumedPart.mutate({
        workOrderId: workOrder.id,
        partId: source.partId,
        data: { unitSellPriceCents: netCents },
      }, mutationHandlers);
      return;
    }
    if (source.kind === 'fixedService') {
      billing.updateServiceDetails.mutate({
        base: detailsBase(workOrder, source.bookingServiceId),
        workOrderId: workOrder.id,
        bookingServiceId: source.bookingServiceId,
        data: payloadAfterFixedPriceOverride(unitPriceGrossCents),
      }, mutationHandlers);
    }
  };

  const handleResetFixedServicePrice = (line: AnnotatedBillingLine) => {
    const source = line.source;
    if (source?.kind !== 'fixedService') return;
    billing.updateServiceDetails.mutate({
        base: detailsBase(workOrder, source.bookingServiceId),
      workOrderId: workOrder.id,
      bookingServiceId: source.bookingServiceId,
      data: payloadAfterFixedPriceOverride(null),
    }, mutationHandlers);
  };

  const handleCommitDiscount = (line: AnnotatedBillingLine, discountPercent: number) => {
    const source = line.source;
    if (!source) return;
    if (source.kind === 'labor') {
      const service = findServiceByBookingId(workOrder, source.bookingServiceId);
      if (!service) return;
      billing.updateServiceDetails.mutate({
        base: detailsBase(workOrder, source.bookingServiceId),
        workOrderId: workOrder.id,
        bookingServiceId: source.bookingServiceId,
        data: payloadAfterLaborPatch(service, source.laborItemId, { discountPercent }),
      }, mutationHandlers);
      return;
    }
    if (source.kind === 'part') {
      const service = findServiceByBookingId(workOrder, source.bookingServiceId);
      if (!service) return;
      billing.updateServiceDetails.mutate({
        base: detailsBase(workOrder, source.bookingServiceId),
        workOrderId: workOrder.id,
        bookingServiceId: source.bookingServiceId,
        data: payloadAfterPartPatch(service, source.partId, { discountPercent }),
      }, mutationHandlers);
      return;
    }
    if (source.kind === 'consumedPart') {
      billing.updateConsumedPart.mutate({
        workOrderId: workOrder.id,
        partId: source.partId,
        data: { discountPercent },
      }, mutationHandlers);
    }
  };

  // Price source and the explicit price lookup of stock-consumed part lines (#3034). A line recorded before sources were kept
  // (priceSource null) is shown as unknown, never as catalog. Nothing here reprices by itself: the lookup is one confirmed click
  // on one line, and it sends `reprice: true` alone (never with a price), so a quantity or discount edit keeps the stored price.
  const consumedPartById = useMemo(
    () => new Map(billing.consumedParts.map((part) => [part.id, part] as const)),
    [billing.consumedParts],
  );
  const consumedPartPrice = (consumedPartId: string): ConsumedPartPriceInfo | undefined => {
    const part = consumedPartById.get(consumedPartId);
    if (!part) return undefined;
    return {
      priceSource: part.priceSource ?? null,
      canReprice: canRepriceLines && typeof part.partId === 'string' && part.partId !== '',
    };
  };

  const catalogPartIdOf = (consumedPartId: string): string | null => {
    const catalogPartId = consumedPartById.get(consumedPartId)?.partId;
    return typeof catalogPartId === 'string' && catalogPartId !== '' ? catalogPartId : null;
  };

  // A reprice confirmation is an intent of ONE owner, on ONE work order, for ONE line, while editing and holding both rights.
  // It is checked in every render, synchronously: the moment the tenant, user, session family or generation, the lock, a right,
  // the work order, the edit state or the line changes, the intent is dropped (state, not just hidden), so returning to a
  // previous owner or state (A -> B -> A, lock -> unlock) never revives it.
  const repriceIntentHolds =
    repriceIntent !== null &&
    pricingAccess.active &&
    ownerEquals(repriceIntent.owner, pricingAccess.owner) &&
    repriceIntent.epoch === pricingAccess.epoch &&
    canRepriceLines &&
    repriceIntent.workOrderId === workOrder.id &&
    editable &&
    catalogPartIdOf(repriceIntent.consumedPartId) === repriceIntent.catalogPartId;
  if (repriceIntent !== null && !repriceIntentHolds) setRepriceIntent(null);
  const activeRepriceIntent = repriceIntentHolds ? repriceIntent : null;

  const handleRequestReprice = (line: AnnotatedBillingLine) => {
    if (line.source?.kind !== 'consumedPart') return;
    const consumedPartId = line.source.partId;
    const catalogPartId = catalogPartIdOf(consumedPartId);
    if (catalogPartId === null || pricingAccess.owner === null) return;
    try {
      // The owner of the intent is the live owner of the render that offered it; refused when either right is gone now.
      takeFence(pricingAccess, REPRICE_PERMISSIONS);
    } catch (error) {
      if (error instanceof PricingAccessError) notify.error(tc('error'), t('detail.partPricing.reprice.forbidden'));
      return;
    }
    setRepriceIntent({
      line,
      workOrderId: workOrder.id,
      consumedPartId,
      catalogPartId,
      owner: pricingAccess.owner,
      epoch: pricingAccess.epoch,
    });
  };

  const handleConfirmReprice = () => {
    const intent = activeRepriceIntent;
    setRepriceIntent(null);
    if (intent === null) return;
    // Checked against the LIVE session now, not the render that opened the dialog: another owner sends nothing and says
    // nothing; a right lost meanwhile sends nothing and is reported to the (same) owner.
    let fence: PricingFence;
    try {
      fence = takeFence({ owner: intent.owner, epoch: intent.epoch }, REPRICE_PERMISSIONS);
    } catch (error) {
      if (error instanceof PricingAccessError) notify.error(tc('error'), t('detail.partPricing.reprice.forbidden'));
      return;
    }
    // What the outcome may still report: same screen, same work order, and the owner and rights the intent began under.
    const mayReport = () => mountedRef.current && workOrderIdRef.current === intent.workOrderId && fenceHolds(fence);
    billing.updateConsumedPart.mutate(
      { workOrderId: intent.workOrderId, partId: intent.consumedPartId, data: { reprice: true } },
      {
        onSuccess: () => {
          if (mayReport()) notify.success(t('detail.partPricing.reprice.success'));
        },
        onError: (error) => {
          if (isSessionCancel(error) || !mayReport()) return;
          notify.error(tc('error'), t(repriceFailureKey(error)));
        },
      },
    );
  };

  const handleConfirmDelete = () => {
    const line = linePendingDelete;
    setLinePendingDelete(null);
    if (!line?.source) return;
    const source = line.source;
    const deleteHandlers = {
      onSuccess: () => notify.success(ti('workOrderBilling.lineDeleted')),
      onError: () => notify.error(ti('workOrderBilling.lineDeleteFailed')),
    };
    if (source.kind === 'labor') {
      const service = findServiceByBookingId(workOrder, source.bookingServiceId);
      if (!service) return;
      billing.updateServiceDetails.mutate({
        base: detailsBase(workOrder, source.bookingServiceId),
        workOrderId: workOrder.id,
        bookingServiceId: source.bookingServiceId,
        data: payloadAfterLaborPatch(service, source.laborItemId, {}, true),
      }, deleteHandlers);
      return;
    }
    if (source.kind === 'part') {
      const service = findServiceByBookingId(workOrder, source.bookingServiceId);
      if (!service) return;
      billing.updateServiceDetails.mutate({
        base: detailsBase(workOrder, source.bookingServiceId),
        workOrderId: workOrder.id,
        bookingServiceId: source.bookingServiceId,
        data: payloadAfterPartPatch(service, source.partId, {}, true),
      }, deleteHandlers);
      return;
    }
    if (source.kind === 'consumedPart') {
      billing.deleteConsumedPart.mutate({
        workOrderId: workOrder.id,
        partId: source.partId,
      }, deleteHandlers);
      return;
    }
    if (source.kind === 'extra') {
      const service = findServiceByBookingId(workOrder, source.bookingServiceId);
      if (!service) return;
      billing.updateServiceDetails.mutate({
        base: detailsBase(workOrder, source.bookingServiceId),
        workOrderId: workOrder.id,
        bookingServiceId: source.bookingServiceId,
        data: payloadAfterExtraPatch(service, source.extraId, {}, true),
      }, deleteHandlers);
      return;
    }
    if (source.kind === 'workOrderItem') {
      billing.deleteManualLine.mutate({
        workOrderId: workOrder.id,
        itemId: source.itemId,
      }, deleteHandlers);
    }
  };

  const handleConfirmBillActual = async () => {
    if (billActualServiceId == null) return;
    try {
      await billActual.mutateAsync({
        workOrderId: workOrder.id,
        bookingServiceId: billActualServiceId,
      });
      notify.success(t('detail.billActualSuccess'));
      setBillActualServiceId(null);
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('detail.billActualFailed') }));
    }
  };

  const handleAddLine = (groupName: string | null, groupSortOrder?: number) => {
    const addHandlers = {
      onSuccess: () => notify.success(ti('workOrderBilling.lineAdded')),
      onError: () => notify.error(ti('workOrderBilling.lineAddFailed')),
    };
    if (groupName) {
      const service = findServiceByName(workOrder, groupName, groupSortOrder);
      // A fixed-price package bills one line; extra work goes outside it (#3655).
      if (service && !isFixedPricePackage(service)) {
        billing.updateServiceDetails.mutate({
        base: detailsBase(workOrder, service.bookingServiceId),
          workOrderId: workOrder.id,
          bookingServiceId: service.bookingServiceId,
          data: payloadAfterAddExtra(service, {
            description: ti('workOrderBilling.newLineDescription'),
            amountGrossCents: 0,
            isTaxable: true,
            hasVariableQuantity: true,
            unit: 'piece',
            quantity: 1,
            sortOrder: service.extras.length,
          }),
        }, addHandlers);
        return;
      }
    }
    billing.addManualLine.mutate({
      workOrderId: workOrder.id,
      data: {
        description: ti('workOrderBilling.newLineDescription'),
        category: 'manual',
        partsCostCents: 0,
        laborCostCents: 0,
      },
    }, addHandlers);
  };

  // One in-flight PATCH at a time; the queue slot holds only the latest cents (#2654).
  flushHourlyRateSaveRef.current = async () => {
    if (laborRateInFlightRef.current) return;
    laborRateInFlightRef.current = true;
    const runnerEpoch = laborRateSaveEpochRef.current;
    try {
      while (laborRateQueuedCentsRef.current != null) {
        if (runnerEpoch !== laborRateSaveEpochRef.current) break;
        const cents = laborRateQueuedCentsRef.current;
        if (
          cents === lastSubmittedRateCentsRef.current
          && lastFailedRateCentsRef.current !== cents
        ) {
          laborRateQueuedCentsRef.current = null;
          break;
        }
        const workOrderId = workOrder.id;
        laborRateQueuedCentsRef.current = null;
        lastFailedRateCentsRef.current = null;
        lastSubmittedRateCentsRef.current = cents;
        setRateUpdating(true);
        try {
          await updateBooking.mutateAsync({
            id: workOrderId,
            data: { laborRateGrossCents: cents },
          });
        } catch {
          if (runnerEpoch !== laborRateSaveEpochRef.current) break;
          if (
            laborRateQueuedCentsRef.current != null
            && laborRateQueuedCentsRef.current !== cents
          ) {
            continue;
          }
          lastFailedRateCentsRef.current = cents;
          notify.error(t('detail.laborRateUpdateFailed'));
          setRateUpdating(false);
          break;
        }
        if (runnerEpoch !== laborRateSaveEpochRef.current) break;
        if (laborRateQueuedCentsRef.current != null) continue;
        const latestDraftCents = parseHourlyRateDraft(rateDraftRef.current);
        if (latestDraftCents != null && latestDraftCents !== cents) {
          dirtyRateRef.current = true;
          break;
        }
        if (latestDraftCents == null && dirtyRateRef.current) {
          break;
        }
        dirtyRateRef.current = false;
        notify.success(t('detail.laborRateUpdated'));
        const refetchEpoch = laborRateSaveEpochRef.current;
        void Promise.resolve(preview.refetch()).finally(() => {
          if (refetchEpoch !== laborRateSaveEpochRef.current) return;
          if (laborRateInFlightRef.current || laborRateQueuedCentsRef.current != null) return;
          setRateUpdating(false);
        });
        break;
      }
    } finally {
      laborRateInFlightRef.current = false;
      if (laborRateQueuedCentsRef.current != null) {
        void flushHourlyRateSaveRef.current();
      }
    }
  };

  persistHourlyRateRef.current = (reason) => {
    const cents = parseHourlyRateDraft(rateDraftRef.current);
    if (cents == null) {
      if (reason === 'blur') {
        dirtyRateRef.current = false;
        setRateUpdating(false);
        setRateDraft(formatHourlyRateCents(displayRateCents));
      }
      return;
    }
    if (reason === 'blur' || reason === 'enter') {
      setRateDraft((cents / 100).toFixed(2));
    }
    if (
      cents === lastSubmittedRateCentsRef.current
      && lastFailedRateCentsRef.current !== cents
    ) {
      laborRateQueuedCentsRef.current = null;
      return;
    }
    if (cents === displayRateCents && lastSubmittedRateCentsRef.current == null) {
      dirtyRateRef.current = false;
      setRateUpdating(false);
      laborRateQueuedCentsRef.current = null;
      return;
    }
    laborRateQueuedCentsRef.current = cents;
    void flushHourlyRateSaveRef.current();
  };

  useEffect(() => {
    if (!editable) return;
    const cents = parseHourlyRateDraft(rateDraft);
    if (cents == null) return;
    if (cents === lastSubmittedRateCentsRef.current) return;
    if (cents === displayRateCents && lastSubmittedRateCentsRef.current == null) {
      dirtyRateRef.current = false;
      setRateUpdating(false);
      return;
    }
    setRateUpdating(true);
    const timer = window.setTimeout(() => {
      persistHourlyRateRef.current('debounce');
    }, LABOR_RATE_SAVE_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [rateDraft, editable, displayRateCents]);

  const hourlyRateField = !editable ? (
    <Box data-testid="billing-hourly-rate-section">
      <Typography variant="subtitle2" gutterBottom>
        {ti('workOrderBilling.hourlyRateSection')}
      </Typography>
      <Typography variant="body2">
        {displayRateCents > 0 ? formatPrice(displayRateCents) : '—'}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {t('detail.hourlyRateHint')}
      </Typography>
    </Box>
  ) : (
    <Box data-testid="billing-hourly-rate-section">
      <Typography variant="subtitle2" gutterBottom>
        {ti('workOrderBilling.hourlyRateSection')}
      </Typography>
      <TextField
        size="small"
        label={t('detail.hourlyRate')}
        value={rateDraft}
        onChange={(e) => {
          const next = e.target.value.replace(/[^0-9.,]/g, '');
          setRateDraft(next);
          dirtyRateRef.current = true;
          const parsed = parseHourlyRateDraft(next);
          setRateUpdating(parsed == null || parsed !== displayRateCents);
        }}
        onBlur={() => persistHourlyRateRef.current('blur')}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          persistHourlyRateRef.current('enter');
        }}
        fullWidth
        {...(rateUpdating
          ? {
              helperText: (
                <span data-testid="billing-hourly-rate-updating">
                  {t('detail.laborRateUpdating')}
                </span>
              ),
            }
          : {})}
        slotProps={{
          htmlInput: {
            'data-testid': 'billing-hourly-rate',
            'aria-busy': rateUpdating,
          },
          input: {
            endAdornment: <InputAdornment position="end">{t('detail.hourlyRateHint')}</InputAdornment>,
          },
        }}
      />
    </Box>
  );

  return (
    <>
      <PaymentLedgerProvider value={paymentLedgerContext}>
      <Card variant={cardVariant} sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
            <Typography variant="h6">
              {t('detail.payments')}
            </Typography>
            {(canReadInvoices || !billingLocked || overflowActions.length > 0) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {canReadInvoices && (
                  // Always available (#2095): before an invoice exists this renders
                  // the would-be invoice straight from the work order; afterwards it
                  // opens the draft or finalized invoice itself.
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PreviewIcon />}
                    data-testid="billing-preview-invoice"
                    onClick={() => {
                      const open = workOrderInvoice
                        ? openInvoicePdf(workOrderInvoice.id)
                        : openInvoicePreviewPdf(workOrder.id, workOrder.vatPercent ?? DEFAULT_VAT_PERCENT);
                      void open.catch((err: unknown) => {
                        notify.error(ti('actions.previewInvoiceFailed'), translateBillingError(err, ti));
                      });
                    }}
                  >
                    {ti('actions.previewInvoice')}
                  </Button>
                )}
                {!billingLocked && (
                  <Button
                    size="small"
                    variant={editMode ? 'contained' : 'outlined'}
                    startIcon={editMode ? <DoneIcon /> : <EditIcon />}
                    data-testid="billing-edit-toggle"
                    onClick={() => setEditMode((current) => !current)}
                  >
                    {editMode ? ti('workOrderBilling.done') : tc('edit')}
                  </Button>
                )}
                {overflowActions.length > 0 && (
                  <>
                    <Tooltip title={tc('moreOptions')}>
                      <IconButton
                        size="small"
                        aria-label={tc('moreOptions')}
                        data-testid="billing-overflow-button"
                        onClick={(event) => setOverflowAnchor(event.currentTarget)}
                        sx={{ minWidth: 36, minHeight: 36 }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      anchorEl={overflowAnchor}
                      open={Boolean(overflowAnchor)}
                      onClose={() => setOverflowAnchor(null)}
                      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    >
                      {overflowActions.map((action) => (
                        <MenuItem
                          key={action.key}
                          data-testid={`billing-overflow-${action.key}`}
                          {...(action.to
                            ? { component: RouterLink, to: action.to }
                            : {})}
                          onClick={() => {
                            setOverflowAnchor(null);
                            action.onClick?.();
                          }}
                        >
                          <ListItemIcon>{action.icon}</ListItemIcon>
                          {action.label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}
              </Box>
            )}
          </Box>
          <WorkOrderConsolidationNotice
            workOrderId={workOrder.id}
            canManage={canManageInvoices}
            canRead={canReadInvoices}
          />
          <BillingLineActionsContext.Provider
            value={{
              resetFixedServicePrice: handleResetFixedServicePrice,
              openServiceLabor: onOpenServiceLabor,
              consumedPartPrice,
              requestReprice: handleRequestReprice,
            }}
          >
            <GroupedInvoiceLines
              lines={annotatedLines}
              formatPrice={formatPrice}
              fallbackTaxPercent={fallbackTaxPercent}
              emptyLabel={billingSourceLoading
                ? t('detail.billingLinesLoading')
                : billingEmpty
                  ? t(workOrder.services.length > 0
                    ? 'detail.noBillableWorkBooked'
                    : 'detail.noBillableWork')
                  : t('detail.noBillingLines')}
              {...(billingEmpty && !billingLocked
                ? {
                    emptyAddLabel: ti('workOrderBilling.addWorkOrPart'),
                    onAddFirstLine: () => {
                      // Same wiring as the Add line button; edit mode opens so the new line can be priced.
                      setEditMode(true);
                      handleAddLine(null);
                    },
                  }
                : {})}
              editable={editable}
              saving={billing.isPending}
              onCommitQuantity={handleCommitQuantity}
              onCommitUnitPrice={handleCommitUnitPrice}
              onCommitDiscount={handleCommitDiscount}
              onDeleteLine={(line) => setLinePendingDelete(line)}
              onAddLine={handleAddLine}
            />
          </BillingLineActionsContext.Provider>
          <BillActualServices
            services={workOrder.services}
            canEditBillableHours={canEditBillableHours}
            billingLocked={hoursBillingLocked}
            pendingServiceId={billActual.isPending ? billActualServiceId : null}
            onRequestBillActual={setBillActualServiceId}
          />
          <Divider sx={{ my: 1.5 }} />
          {billingTotals && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('detail.billingSubtotal')}
                </Typography>
                <Typography variant="body2">{formatPrice(billingTotals.subtotalCents)}</Typography>
              </Box>
              {(billingTotals.taxBreakdown ?? []).filter((group) => group.taxPercent > 0).length > 0
                ? (billingTotals.taxBreakdown ?? [])
                  .filter((group) => group.taxPercent > 0)
                  .map((group) => (
                    <Box key={group.taxPercent} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {`${t('detail.billingTax')} ${String(group.taxPercent).replace('.', ',')} %`}
                      </Typography>
                      <Typography variant="body2">{formatPrice(group.taxCents)}</Typography>
                    </Box>
                  ))
                : (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('detail.billingTax')}
                    </Typography>
                    <Typography variant="body2">{formatPrice(billingTotals.taxCents)}</Typography>
                  </Box>
                )}
            </>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {t('total')}
            </Typography>
            <Typography variant="subtitle1" fontWeight={600}>
              {displayTotalCents != null ? formatPrice(displayTotalCents) : '—'}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {t('detail.vatIncluded')}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <WorkOrderSurchargeRow workOrder={workOrder} locked={billingLocked} />
          <Divider sx={{ my: 2 }} />
          {hourlyRateField}
          {workOrder.isDisputed && (
            <Alert severity="warning" sx={{ mt: 2 }} data-testid="billing-dispute-reason">
              {t('workOrderActions.disputeReason')}: {workOrder.disputeReason || '—'}
            </Alert>
          )}
          {workOrder.isInvoicingHold && (
            <Alert severity="info" sx={{ mt: 2 }} data-testid="billing-invoicing-hold-reason">
              {t('workOrderActions.invoicingHoldReason')}: {workOrder.invoicingHoldReason || '—'}
            </Alert>
          )}
          {showCreditedReinvoiceHint && (
            <Alert severity="info" sx={{ mt: 2 }} data-testid="billing-credited-reinvoice">
              {ti('credit.reinvoiceHint')}
              {billingRecipientName ? (
                <span data-testid="billing-credited-recipient">
                  {` ${ti('credit.reinvoiceRecipient', { name: billingRecipientName })}`}
                </span>
              ) : null}
            </Alert>
          )}
          {voidErrorOfferCredit && (
            <Alert
              severity="warning"
              sx={{ mt: 2 }}
              data-testid="billing-void-credit-hint"
              action={invoiceDetail.data ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setCreditOpen(true)}
                >
                  {ti('actions.creditNote')}
                </Button>
              ) : undefined}
            >
              {voidErrorOfferCredit}
            </Alert>
          )}
          {primaryAction && (
            <Button
              variant="contained"
              color="primary"
              data-testid="billing-primary-action"
              startIcon={primaryAction.kind === 'pay'
                ? <PaymentIcon />
                : <ReceiptLongIcon />}
              onClick={handlePrimaryAction}
              disabled={primaryAction.disabled}
              sx={{ mt: 2 }}
            >
              {primaryAction.label}
            </Button>
          )}
          {primaryAction?.kind === 'pay' && canSendAsInvoice && (
            <Button
              variant="outlined"
              startIcon={<SendIcon />}
              data-testid="billing-send-as-invoice"
              onClick={handleSendAsInvoice}
              disabled={deliverInvoice.isPending}
              sx={{ mt: 2, ml: 1 }}
            >
              {t('serviceActions.sendAsInvoice')}
            </Button>
          )}
          {primaryAction?.kind === 'pay' && hasReversedPayment && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              {t('serviceActions.paymentReversedHint')}
            </Typography>
          )}
          {!workOrderInvoice && historyDetail.data?.payments && historyDetail.data.payments.length > 0 && (
            <PaymentHistorySection
              payments={historyDetail.data.payments}
              remainingCents={0}
            />
          )}
        </CardContent>
      </Card>
      {workOrderInvoice && (
        <InvoiceSummaryCard
          invoice={workOrderInvoice}
          cardVariant={cardVariant}
        />
      )}
      {/* Link status, re-send and copy for the work order's invoice (#2978).
          Same section as the invoice page; it hides itself unless the invoice
          is open and Svea is enabled. */}
      {invoiceDetail.data && (
        <SveaPaymentSection invoice={invoiceDetail.data} canManage={canManageInvoices} />
      )}

      {invoicesHistory.data && invoicesHistory.data.length > 0 && (
        <PreviousInvoicesSection invoices={invoicesHistory.data} />
      )}
      </PaymentLedgerProvider>

      <ConfirmDialog
        open={linePendingDelete != null}
        title={ti('workOrderBilling.deleteLineTitle')}
        message={ti('workOrderBilling.deleteLineMessage')}
        confirmColor="error"
        onConfirm={handleConfirmDelete}
        onCancel={() => setLinePendingDelete(null)}
        loading={billing.isPending}
      />

      <ConfirmDialog
        open={activeRepriceIntent !== null}
        title={t('detail.partPricing.reprice.title')}
        message={t('detail.partPricing.reprice.message')}
        confirmText={t('detail.partPricing.reprice.confirm')}
        confirmColor="warning"
        onConfirm={handleConfirmReprice}
        onCancel={() => setRepriceIntent(null)}
        loading={billing.isPending}
      />

      <ConfirmDialog
        open={billActualServiceId != null}
        title={t('detail.billActualConfirmTitle')}
        message={t('detail.billActualConfirmMessage', { hours: billActualHoursLabel })}
        confirmText={t('detail.billActual', { hours: billActualHoursLabel })}
        onConfirm={() => { void handleConfirmBillActual(); }}
        onCancel={() => {
          if (!billActual.isPending) setBillActualServiceId(null);
        }}
        loading={billActual.isPending}
      />

      <PreInvoiceChecklistDialog
        open={checklistOpen}
        onClose={() => setChecklistOpen(false)}
        onConfirm={handleChecklistConfirm}
        workOrderId={workOrder.id}
        finalInspectionCompleted={workOrder.finalInspectionCompleted === true}
        laborCount={laborCount}
        partsCount={partsCount}
        stage="invoice"
      />

      <GenerateInvoiceDialog
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        onCreated={setPostInvoiceDocuments}
        workOrderId={workOrder.id}
        customerEmail={customerEmail}
        taxPercent={fallbackTaxPercent}
        {...(displayTotalCents != null ? { totalCents: displayTotalCents } : {})}
        {...(workOrder.vehicle?.registrationNumber
          ? { vehicleRegistration: workOrder.vehicle.registrationNumber }
          : {})}
      />

      <PostInvoiceDocumentsDialog
        open={postInvoiceDocuments !== null}
        onClose={() => setPostInvoiceDocuments(null)}
        workOrder={workOrder}
        {...(postInvoiceDocuments
          ? {
              invoiceId: postInvoiceDocuments.invoiceId,
              ...(postInvoiceDocuments.receiptId
                ? { receiptId: postInvoiceDocuments.receiptId }
                : {}),
            }
          : {})}
      />

      <RecordPaymentDialog
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
        workOrderId={workOrder.id}
        totalCents={workOrderInvoice?.totalCents ?? displayTotalCents ?? 0}
        paidAmountCents={workOrderInvoice?.paidAmountCents ?? 0}
        customerEmail={customerEmail}
        {...(workOrderInvoice
          ? { invoiceId: workOrderInvoice.id, invoiceNumber: workOrderInvoice.invoiceNumber }
          : {})}
      />

      <ConfirmDialog
        open={voidOpen}
        title={ti('actions.voidTitle')}
        message={ti('actions.voidConfirm', { invoiceNumber: workOrderInvoice?.invoiceNumber ?? '' })}
        confirmText={ti('actions.void')}
        confirmColor="error"
        onConfirm={() => void handleVoid()}
        onCancel={() => setVoidOpen(false)}
        loading={voidInvoice.isPending}
      />

      {creditOpen && invoiceDetail.data && (
        <CreditNoteDialog
          open={creditOpen}
          onClose={() => setCreditOpen(false)}
          invoice={invoiceDetail.data}
        />
      )}
    </>
  );
}
